import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  CurrencyCode,
  QuoteStatus,
  UserRole,
  convertQuoteSchema,
  convertUsdMinorToVesMinor,
  formatMoney,
} from '@lombok/shared';
import type { OrderItem, Quote, UserRole as UserRoleType } from '@lombok/shared';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@lombok/ui';
import { AccountName } from '@/components/AccountName';
import { WIDE_COLUMN } from '@/components/layout';
import { PageHeader } from '@/components/PageHeader';
import { BackLink, DetailRow } from '@/components/DetailRow';
import { StatusPill } from '@/components/StatusPill';
import { QueryState } from '@/components/QueryState';
import { FormError, TextField, TextareaField } from '@/components/form';
import { useSessionProfile } from '@/auth/useSessionProfile';
import { parseDecimal } from '@/lib/decimal';
import { quoteLabel } from '@/lib/names';
import {
  useAcceptQuote,
  useConvertQuote,
  useQuote,
  useRejectQuote,
  useSendQuote,
} from '@/data/hooks';

/**
 * Internal roles that may act on a quote (send / accept / reject / convert).
 * Mirrors the backend's order-create authorization (admin + sales) — a UX gate
 * only; the backend re-checks every transition and stays the authority.
 */
const QUOTE_ACTOR_ROLES: readonly UserRoleType[] = [
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  UserRole.SALES_MANAGER,
  UserRole.SALES_REP,
];

interface CotizacionDetailPageProps {
  /** Group root for the "Cotizaciones" back link, e.g. "/app/admin/cotizaciones". */
  basePath: string;
  /** Sibling "pedidos" route under the same group root (where a converted quote lands). */
  ordersBasePath: string;
  backLabel?: string;
}

/**
 * Cotización detail: header + items (line/total money) + a status/validity
 * summary. Role-gated ACTIONS — Enviar (draft), Aceptar/Rechazar (sent), and
 * Convertir a pedido (accepted/sent, with the VES/USD rate the new order locks).
 * Actions are UX affordances only; the backend re-checks each transition.
 */
export function CotizacionDetailPage({
  basePath,
  ordersBasePath,
  backLabel = 'Cotizaciones',
}: CotizacionDetailPageProps) {
  const { id } = useParams<{ id: string }>();
  const query = useQuote(id);
  const quote = query.data;

  return (
    <div className={`flex flex-col gap-6 ${WIDE_COLUMN}`}>
      <div className="flex flex-col gap-2">
        <BackLink to={basePath}>{backLabel}</BackLink>
        <PageHeader
          title={quote ? `Cotización ${quoteLabel(quote)}` : 'Cotización'}
          description="Detalle, montos y estado."
          actions={quote ? <StatusPill kind="quote" value={quote.status} /> : undefined}
        />
      </div>

      <QueryState
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
        loadingLabel="Cargando cotización…"
      >
        {quote ? (
          <div className="flex flex-col gap-6">
            <QuoteActions quote={quote} ordersBasePath={ordersBasePath} />
            <QuoteItemsCard quote={quote} />
            <QuoteSummaryCard quote={quote} />
          </div>
        ) : null}
      </QueryState>
    </div>
  );
}

/** Items table with per-line and total money (shared formatMoney). USD only. */
function QuoteItemsCard({ quote }: { quote: Quote }) {
  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle>Artículos</CardTitle>
      </CardHeader>
      <CardContent className="px-0 pb-0 pt-0">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border text-xs uppercase tracking-wide text-text-muted">
                <th className="px-4 py-3 font-medium">Producto</th>
                <th className="px-4 py-3 font-medium">SKU</th>
                <th className="px-4 py-3 text-right font-medium">Cant.</th>
                <th className="px-4 py-3 text-right font-medium">Precio</th>
                <th className="px-4 py-3 text-right font-medium">Total línea</th>
              </tr>
            </thead>
            <tbody>
              {quote.items.map((it: OrderItem) => (
                <tr key={it.lineNumber} className="border-b border-border last:border-0">
                  <td className="px-4 py-3 font-medium text-text-primary">{it.productName}</td>
                  <td className="px-4 py-3 font-mono text-xs text-text-muted">{it.skuCode}</td>
                  <td className="px-4 py-3 text-right text-text-muted">{it.quantity}</td>
                  <td className="px-4 py-3 text-right text-text-muted">
                    {formatMoney(it.unitPriceMinorUsd)}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-text-primary">
                    {formatMoney(it.lineTotalMinorUsd)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-border">
                <td className="px-4 py-3 font-semibold text-text-primary" colSpan={4}>
                  Total (USD)
                </td>
                <td className="px-4 py-3 text-right text-lg font-semibold text-text-primary">
                  {formatMoney(quote.totalMinorUsd)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

/** Quote metadata (client, vendor, validity, dates). No locked rate — quotes are USD. */
function QuoteSummaryCard({ quote }: { quote: Quote }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Resumen</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <dl className="flex flex-col">
          <DetailRow label="Cliente">
            <AccountName id={quote.customerId} />
          </DetailRow>
          <DetailRow label="Vendedor">{quote.salesRepId ?? '—'}</DetailRow>
          <DetailRow label="Estado">
            <StatusPill kind="quote" value={quote.status} />
          </DetailRow>
          <DetailRow label="Vence">{formatDate(quote.validUntil)}</DetailRow>
          <DetailRow label="Creada">{formatDateTime(quote.createdAt)}</DetailRow>
          <DetailRow label="Última actualización">{formatDateTime(quote.updatedAt)}</DetailRow>
        </dl>
      </CardContent>
    </Card>
  );
}

/**
 * Role- and status-aware action bar. Enviar (draft), Aceptar/Rechazar (sent),
 * and Convertir a pedido (accepted/sent). Each is purely a UX affordance — the
 * backend re-checks every transition.
 */
function QuoteActions({ quote, ordersBasePath }: { quote: Quote; ordersBasePath: string }) {
  const { profile } = useSessionProfile();
  const role = profile?.role;

  const send = useSendQuote(quote.quoteId);
  const accept = useAcceptQuote(quote.quoteId);
  const reject = useRejectQuote(quote.quoteId);

  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);

  if (!role) return null;

  const isActor = QUOTE_ACTOR_ROLES.includes(role);
  if (!isActor) return null;

  const canSend = quote.status === QuoteStatus.DRAFT;
  const canDecide = quote.status === QuoteStatus.SENT;
  const canConvert =
    quote.status === QuoteStatus.ACCEPTED || quote.status === QuoteStatus.SENT;

  if (!canSend && !canDecide && !canConvert) return null;

  const pending = send.isPending || accept.isPending || reject.isPending;

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 pt-5">
        <div className="flex flex-wrap gap-2">
          {canSend ? (
            <Button onClick={() => send.mutate({})} disabled={pending}>
              {send.isPending ? 'Enviando…' : 'Enviar'}
            </Button>
          ) : null}

          {canDecide ? (
            <>
              <Button onClick={() => accept.mutate({})} disabled={pending}>
                {accept.isPending ? 'Aceptando…' : 'Aceptar'}
              </Button>
              <Button variant="outline" onClick={() => setShowReject((v) => !v)} disabled={pending}>
                Rechazar
              </Button>
            </>
          ) : null}
        </div>

        {canDecide && showReject ? (
          <div className="flex flex-col gap-3 rounded-md border border-border p-3">
            <TextareaField
              label="Motivo del rechazo"
              placeholder="Explica por qué se rechaza (opcional)."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowReject(false)}
                disabled={pending}
              >
                Cerrar
              </Button>
              <Button
                size="sm"
                onClick={() =>
                  reject.mutate(
                    { reason: rejectReason.trim() || undefined },
                    { onSuccess: () => setShowReject(false) },
                  )
                }
                disabled={pending}
              >
                {reject.isPending ? 'Rechazando…' : 'Confirmar rechazo'}
              </Button>
            </div>
          </div>
        ) : null}

        {canConvert ? <ConvertPanel quote={quote} ordersBasePath={ordersBasePath} /> : null}
      </CardContent>
    </Card>
  );
}

/**
 * "Convertir a pedido" — turns the quote into a real order. The order locks the
 * VES/USD exchange rate entered here (the quote itself is USD). On success we
 * jump to the created order's detail and also surface its link. Idempotent
 * server-side per attempt via the generated key; the backend re-checks the
 * quote is convertible.
 */
function ConvertPanel({ quote, ordersBasePath }: { quote: Quote; ordersBasePath: string }) {
  const navigate = useNavigate();
  const convert = useConvertQuote(quote.quoteId);

  const [open, setOpen] = useState(false);
  const [rateText, setRateText] = useState('');
  const [createdOrderId, setCreatedOrderId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const rate = parseDecimal(rateText);
  const rateValid = Number.isFinite(rate) && rate > 0;
  const vesTotal = rateValid ? convertUsdMinorToVesMinor(quote.totalMinorUsd, rate) : 0;

  // Already converted in this session: show the resulting order link.
  if (createdOrderId) {
    return (
      <div className="flex flex-col gap-3 rounded-md border border-success-soft bg-success-soft/40 p-3">
        <p className="text-sm font-medium text-text-primary">
          La cotización se convirtió en un pedido.
        </p>
        <div className="flex items-center justify-between gap-3">
          <span className="font-mono text-sm text-text-muted">{createdOrderId}</span>
          <Link to={`${ordersBasePath}/${createdOrderId}`}>
            <Button size="sm">Ver pedido</Button>
          </Link>
        </div>
      </div>
    );
  }

  const onConvert = () => {
    setFormError(null);
    const parsed = convertQuoteSchema.safeParse({ exchangeRate: rate });
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? 'Ingresa una tasa de cambio válida.');
      return;
    }
    convert.mutate(parsed.data, {
      onSuccess: (result) => {
        setCreatedOrderId(result.orderId);
        navigate(`${ordersBasePath}/${result.orderId}`);
      },
    });
  };

  if (!open) {
    return (
      <div className="border-t border-border pt-4">
        <Button variant="outline" onClick={() => setOpen(true)}>
          Convertir a pedido
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 rounded-md border border-border p-4">
      <div>
        <p className="text-sm font-medium text-text-primary">Convertir a pedido</p>
        <p className="text-xs text-text-muted">
          El pedido fija esta tasa de cambio (VES por USD) y reserva inventario.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          label="Tasa de cambio (VES por USD)"
          inputMode="decimal"
          required
          placeholder="Ej. 40,00"
          value={rateText}
          onChange={(e) => setRateText(e.target.value)}
        />
      </div>

      <div className="flex flex-col gap-2 rounded-md bg-surface-muted p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-text-muted">Total (USD)</span>
          <span className="text-base font-semibold text-text-primary">
            {formatMoney(quote.totalMinorUsd)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-text-muted">Equivalente (VES)</span>
          <span className="text-base font-medium text-text-primary">
            {rateValid ? formatMoney(vesTotal, CurrencyCode.VES) : '—'}
          </span>
        </div>
      </div>

      <FormError error={convert.error} />
      {formError ? (
        <p className="text-sm text-danger" role="alert">
          {formError}
        </p>
      ) : null}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpen(false)}
          disabled={convert.isPending}
        >
          Cancelar
        </Button>
        <Button size="sm" onClick={onConvert} disabled={!rateValid || convert.isPending}>
          {convert.isPending ? 'Convirtiendo…' : 'Convertir a pedido'}
        </Button>
      </div>
    </div>
  );
}

/** Compact local date (es-VE). `—` when absent; raw value if unparseable. */
function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Local date-time (es-VE); raw value fallback if unparseable. */
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('es-VE', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
}
